
import { cloneDeep, throttle } from 'lodash';
import { mapState, mapMutations, mapGetters } from 'vuex';
import MediaService from './MediaService';

export default {
  name: 'DashAudioPlayer',
  props: {
    id: {
      type: String,
      default: 'player',
    },
    params: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      media: null,
    };
  },
  computed: {
    ...mapState('playback', { activePlayback: 'active' }),
    ...mapState('playback', ['error', 'streams', 'volume', 'action', 'autounmute']),
    ...mapGetters('playback', ['isLiveStream']),

    isEmptySrc() {
      return !this.streams?.length;
    },
  },
  methods: {
    ...mapMutations('playback', [
      'setActive',
      'setPlaying',
      'setBuffering',
      'setDuration',
      'setCurrentTime',
      'setError',
      'setMuted',
      'resetPlayback',
    ]),

    load(streams) {
      this.setDuration(0);
      this.setBuffering(this.error || !this.isEmptySrc);
      this.setError(null);
      this.setPlaying(false);

      if (this.isEmptySrc) this.media.pause();
      // Load streams even if they empty to reload player state
      this.media?.loadSrc(cloneDeep(streams));
    },

    mute(mute) {
      if (this.media.muted !== mute) this.media.setMuted(mute);
      this.setMuted(mute);
    },
  },
  watch: {
    /* Trigger player playback for streams with finite duration (podcasts),
       toggle mute for live streams */
    activePlayback(playback) {
      if (this.error) {
        if (playback) this.load(this.streams);
        return;
      }

      if (playback && this.media.paused) this.media.play();
      if (!playback && !this.isLiveStream) this.media.pause();
      if (this.autounmute) this.mute(this.isLiveStream && !playback);
    },
    streams(streams) {
      this.load(streams);
    },
    volume(volume) {
      this.media.setVolume(volume);
    },
    action(action) {
      const { name, value } = action;
      if (typeof this.media[name] === 'function') this.media[name](value);
    },
  },
  mounted() {
    const callbacks = {
      onPlaying: () => {
        this.setPlaying(this.activePlayback);
        this.setBuffering(false);
        if (!this.isLiveStream && this.autounmute) this.mute(false);
      },
      onPause: () => {
        this.setPlaying(false);
      },
      onCanPlay: () => {
        this.setBuffering(false);
        if (this.activePlayback) {
          this.media.play();
          if (this.autounmute) this.mute(false);
        }
      },
      onVolumeChange: () => {
        this.setPlaying(this.activePlayback);
      },
      onTimeUpdate: throttle(() => {
        this.setCurrentTime(this.media.currentTime);
      }, 500),
      onError: (error) => {
        this.setDuration(0);
        this.setError(error);
        this.setBuffering(false);
        // Reset "active" state on "src load error" to let reload
        if (!this.isEmptySrc) this.setActive(false);
      },
      onLoadMetaData: () => {
        this.setDuration(this.media.duration);
      },
      onEnded: () => {
        // Reset "active" state if audio ended
        if (!this.isLiveStream) this.setActive(false);
      },
    };
    this.media = new MediaService(callbacks, this.id, this.params);
  },
  beforeDestroy() {
    this.resetPlayback();
    this.media.purge();
  },
};
